import { Box, Divider, useTheme } from '@mui/material';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Settings } from '@frontend/features/agencies/components/Settings';
import { SettingsFields } from '@bandido/shared/types';
import { GupshupSubscriptions } from '@frontend/features/whatsapp/components/GupShupSubscriptions';
import { WhatsappQuickRepliesSection } from '@frontend/features/whatsapp/components/WhatsappQuickRepliesSection';
import { WhatsappQuickReplyVariablesHelp } from '@frontend/features/whatsapp/components/WhatsappQuickReplyVariablesHelp';
import { WhatsappTemplatesSection } from '@frontend/features/whatsapp/components/WhatsappTemplatesSection';
import { useGetSettingsByAgencyId } from '@frontend/features/agencies/queries';
import { useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';

export const WhatsappSettingsView = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: settings } = useGetSettingsByAgencyId({ id });
  const { data: workflowsList } = useGetWorkflowsByAgency({
    pageSize: 1000,
    currentPage: 1,
    search: '',
    agencyId: id,
  });
  const GUPSHUP_API_KEY_FIELD =
    SettingsFields.GUPSHUP_API_KEY ?? ('GUPSHUP_API_KEY' as SettingsFields);

  return (
    <Box
      sx={{
        borderRadius: '24px',
        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        height: 'calc(100vh - 180px)',
        overflow: 'hidden',
        padding: theme.spacing(1, 3),
      }}
    >
      <OverlayScrollbarsComponent
        style={{
          marginTop: theme.spacing(5),
          boxSizing: 'border-box',
          width: '100%',
          overflowY: 'hidden',
          height: 'calc(100vh - 160px)',
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'leave', theme: 'os-theme-light' } }}
        defer
      >
        <Box display="flex" flexDirection="column" gap={5}>
          <Settings
            title={t('agencies.settings.whatsapp')}
            agencyId={Number(id)}
            fields={[
              SettingsFields.WHATSAPP_DEFAULT_WORKFLOW_ID,
              SettingsFields.GUPSHUP_APP_ID,
              SettingsFields.GUPSHUP_APP_NAME,
              SettingsFields.GUPSHUP_SENDER_NAME,
              SettingsFields.GUPSHUP_PHONE_NUMBER,
              SettingsFields.GUPSHUP_PARTNER_API_URL,
              SettingsFields.GUPSHUP_AUTH_USER,
              SettingsFields.GUPSHUP_AUTH_SECRET,
              GUPSHUP_API_KEY_FIELD,
            ]}
            values={settings}
            options={{
              fieldTypes: [
                {
                  id: SettingsFields.WHATSAPP_DEFAULT_WORKFLOW_ID,
                  type: 'select',
                  options: workflowsList ? workflowsList.items.map((opt) => ({ label: opt.name, value: opt.id })) : [],
                },
                {
                  id: SettingsFields.GUPSHUP_AUTH_SECRET,
                  type: 'password',
                },
                {
                  id: GUPSHUP_API_KEY_FIELD,
                  type: 'password',
                },
              ],
            }}
          />

          <Divider />
          <WhatsappTemplatesSection agencyId={Number(id)} />

          <Divider />
          <WhatsappQuickReplyVariablesHelp />

          <WhatsappQuickRepliesSection agencyId={Number(id)} />

          <Divider />
          <GupshupSubscriptions agencyId={Number(id)} />
        </Box>
      </OverlayScrollbarsComponent>
    </Box>
  );
};
